import {
  Layers,
  Cpu,
  Activity,
  CheckCircle2,
  Loader2,
  TrendingUp,
  Zap,
  Lock,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import baseapi from '../../../services/baseapi';

interface MongoCollectionRow {
  collection?: string;
  database?: string;
  db_name?: string;
  docs_count?: number;
  size_mb?: number;
  index_size_mb?: number;
  total_size_mb?: number;
  index_count?: number;
}

interface MongoCollectionsViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
}

// const fmtSize = (mb: number | null | undefined) => {
//   if (mb == null || mb <= 0) return '—';
//   if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
//   if (mb >= 1) return `${mb.toFixed(2)} MB`;
//   return `${(mb * 1024).toFixed(0)} KB`;
// };

// const fmtCompact = (n: number | null | undefined) => {
//   if (n == null) return '—';
//   if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
//   if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
//   return n.toLocaleString();
// };

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[10px] truncate max-w-[130px] font-mono" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const MongoCollectionsView: React.FC<MongoCollectionsViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<any>(null);
  const [rows, setRows] = useState<MongoCollectionRow[]>([]);
  const [mongoStatsGraph, setMongoStatsGraph] = useState<any>(null);
  const [connGraph, setConnGraph] = useState<any>(null);

  // Hover States for Telemetry Graphs
  const [hoveredOps, setHoveredOps] = useState<string | null>(null);
  const [hoveredCacheHit, setHoveredCacheHit] = useState<string | null>(null);
  const [hoveredMemory, setHoveredMemory] = useState<string | null>(null);
  const [hoveredConn, setHoveredConn] = useState<string | null>(null);
  const [hoveredReplLag, setHoveredReplLag] = useState<string | null>(null);
  const [hoveredPageFaults, setHoveredPageFaults] = useState<string | null>(null);
  const [hoveredLockWaits, setHoveredLockWaits] = useState<string | null>(null);

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const params: any = { cluster_id: activeCluster };
    if (clientId) params.client_id = clientId;

    const safeGet = async (url: string, p: any) => {
      try {
        const res = await baseapi.get(url, { params: p });
        return res.data?.data || res.data;
      } catch {
        return null;
      }
    };

    try {
      const [mRes, rowsRes, statsRes, connRes] = await Promise.all([
        safeGet('/db_monitor_dashboard/metrics', params),
        safeGet('/db_monitor_dashboard/per_db/overview', params),
        safeGet('/db_monitor_dashboard/graph/mongodb/stats', { ...params, minutes: timeRange }),
        safeGet('/db_monitor_dashboard/graph/connections', { ...params, minutes: timeRange }),
      ]);

      setMetrics(mRes);
      setRows(Array.isArray(rowsRes) ? rowsRes : []);
      setMongoStatsGraph(statsRes);
      setConnGraph(connRes);
    } catch (err) {
      console.warn('MongoCollectionsView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId, timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Graph Data Formatting
  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      return isNaN(d.getTime())
        ? lbl
        : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    });
  };

  const opsFormatted = useMemo(() => {
    if (!mongoStatsGraph?.labels) return [];
    const timeLabels = formatTimeLabels(mongoStatsGraph.labels);
    return mongoStatsGraph.labels.map((_: any, idx: number) => ({
      time: timeLabels[idx],
      'Reads/s': mongoStatsGraph.series?.read_ops_rate?.[idx] || 0,
      'Writes/s': mongoStatsGraph.series?.write_ops_rate?.[idx] || 0,
    }));
  }, [mongoStatsGraph]);

  const cacheHitFormatted = useMemo(() => {
    if (!mongoStatsGraph?.labels) return [];
    const timeLabels = formatTimeLabels(mongoStatsGraph.labels);
    return mongoStatsGraph.labels.map((_: any, idx: number) => ({
      time: timeLabels[idx],
      'Cache Hit %': mongoStatsGraph.series?.cache_hit_pct?.[idx] || 0,
    }));
  }, [mongoStatsGraph]);

  const memoryFormatted = useMemo(() => {
    if (!mongoStatsGraph?.labels) return [];
    const timeLabels = formatTimeLabels(mongoStatsGraph.labels);
    return mongoStatsGraph.labels.map((_: any, idx: number) => ({
      time: timeLabels[idx],
      'Resident MB': mongoStatsGraph.series?.memory_used_mb?.[idx] || 0,
      'Virtual MB': mongoStatsGraph.series?.memory_peak_mb?.[idx] || 0,
      'Cache Pool MB': mongoStatsGraph.series?.memory_buffer_pool_mb?.[idx] || 0,
    }));
  }, [mongoStatsGraph]);

  const replLagFormatted = useMemo(() => {
    if (!mongoStatsGraph?.labels) return [];
    const timeLabels = formatTimeLabels(mongoStatsGraph.labels);
    return mongoStatsGraph.labels.map((_: any, idx: number) => ({
      time: timeLabels[idx],
      'Lag (s)': mongoStatsGraph.series?.replication_lag_sec?.[idx] || 0,
    }));
  }, [mongoStatsGraph]);

  const pageFaultsFormatted = useMemo(() => {
    if (!mongoStatsGraph?.labels) return [];
    const timeLabels = formatTimeLabels(mongoStatsGraph.labels);
    return mongoStatsGraph.labels.map((_: any, idx: number) => ({
      time: timeLabels[idx],
      'Page Faults/s': mongoStatsGraph.series?.page_faults_rate?.[idx] || 0,
    }));
  }, [mongoStatsGraph]);

  const lockWaitsFormatted = useMemo(() => {
    if (!mongoStatsGraph?.labels) return [];
    const timeLabels = formatTimeLabels(mongoStatsGraph.labels);
    return mongoStatsGraph.labels.map((_: any, idx: number) => ({
      time: timeLabels[idx],
      'Lock Waits': mongoStatsGraph.series?.lock_waits?.[idx] || 0,
    }));
  }, [mongoStatsGraph]);

  const connFormatted = useMemo(() => {
    if (!connGraph?.labels) return [];
    const timeLabels = formatTimeLabels(connGraph.labels);
    return connGraph.labels.map((_: any, idx: number) => ({
      time: timeLabels[idx],
      'Active Ops': connGraph.series?.connections_active?.[idx] || 0,
      'Connected Clients': connGraph.series?.connections_total?.[idx] || 0,
    }));
  }, [connGraph]);

  const hasData = (arr: any[]) => arr && arr.length > 0;

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[400px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  const health = metrics?.cluster_health || 'healthy';

  return (
    <div className="space-y-4">
      {/* Header View */}
      <div className="border-b border-gray-800 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-400">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white tracking-wide">
              MongoDB Collection Breakdown
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Per-collection storage, document counts, index statistics, and real-time MongoDB operations telemetry.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 font-mono text-xs">
          <span className="px-2.5 py-1 bg-gray-800/80 border border-gray-700/80 rounded-lg text-gray-300">
            Collections: <strong className="text-white">{rows.length}</strong>
          </span>
          <span className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-bold rounded-lg uppercase">
            {health}
          </span>
        </div>
      </div>

      {/* MongoDB Collections Table */}
      {/* <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-hidden">
        <div className="px-3.5 py-2.5 border-b border-gray-700/60 flex items-center justify-between bg-gray-900/90">
          <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2 font-mono">
            <Layers className="w-4 h-4 text-orange-500" />
            MongoDB Collections ({rows.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-gray-700/80 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-900/90">
                <th className="py-2.5 px-3.5">COLLECTION</th>
                <th className="py-2.5 px-3.5">DATABASE</th>
                <th className="py-2.5 px-3.5 text-right">DOCUMENTS</th>
                <th className="py-2.5 px-3.5 text-right">DATA SIZE</th>
                <th className="py-2.5 px-3.5 text-right">INDEX SIZE</th>
                <th className="py-2.5 px-3.5 text-right">TOTAL SIZE</th>
                <th className="py-2.5 px-3.5 text-right">INDEXES</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/60 font-mono text-xs">
              {rows.length > 0 ? (
                rows.map((r, i) => (
                  <tr key={i} className="hover:bg-orange-500/[0.06] transition-colors">
                    <td className="py-2 px-3.5 font-bold text-white flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                      {r.collection || r.database || '—'}
                    </td>
                    <td className="py-2 px-3.5 text-gray-400">{r.db_name || '—'}</td>
                    <td className="py-2 px-3.5 text-right text-blue-400 font-bold">
                      {fmtCompact(r.docs_count || 0)}
                    </td>
                    <td className="py-2 px-3.5 text-right text-gray-300">{fmtSize(r.size_mb)}</td>
                    <td className="py-2 px-3.5 text-right text-gray-400">{fmtSize(r.index_size_mb)}</td>
                    <td className="py-2 px-3.5 text-right text-orange-300 font-bold">
                      {fmtSize(r.total_size_mb)}
                    </td>
                    <td className="py-2 px-3.5 text-right text-purple-400 font-bold">
                      {r.index_count != null ? r.index_count : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-xs text-gray-500 italic">
                    No collection data reported for this MongoDB cluster.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div> */}

      {/* MongoDB Activity Telemetry Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Operations Rate (Reads vs Writes) */}
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4">
          <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
              Operations Rate (ops/s)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(opsFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={opsFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredOps} onHoverSeries={setHoveredOps} />} />
                  <Line type="monotone" dataKey="Reads/s" stroke="#10b981" strokeOpacity={getOpacity(hoveredOps, 'Reads/s')} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Writes/s" stroke="#3b82f6" strokeOpacity={getOpacity(hoveredOps, 'Writes/s')} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No operation rate telemetry</span>
            )}
          </div>
        </div>

        {/* Cache Hit % */}
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" />
              WiredTiger Cache Hit %
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(cacheHitFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cacheHitFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} domain={[0, 100]} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredCacheHit} onHoverSeries={setHoveredCacheHit} />} />
                  <Line type="monotone" dataKey="Cache Hit %" stroke="#f59e0b" strokeOpacity={getOpacity(hoveredCacheHit, 'Cache Hit %')} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No cache hit telemetry</span>
            )}
          </div>
        </div>

        {/* Memory Allocation */}
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
              <Cpu className="w-3.5 h-3.5 text-purple-400" />
              Memory Allocation (MB)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(memoryFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={memoryFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredMemory} onHoverSeries={setHoveredMemory} />} />
                  <Line type="monotone" dataKey="Resident MB" stroke="#10b981" strokeOpacity={getOpacity(hoveredMemory, 'Resident MB')} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Virtual MB" stroke="#94a3b8" strokeOpacity={getOpacity(hoveredMemory, 'Virtual MB')} strokeDasharray="3 3" strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="Cache Pool MB" stroke="#a855f7" strokeOpacity={getOpacity(hoveredMemory, 'Cache Pool MB')} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No memory telemetry</span>
            )}
          </div>
        </div>

        {/* Connection Activity */}
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-blue-400" />
              Connection Activity (over time)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(connFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={connFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredConn} onHoverSeries={setHoveredConn} />} />
                  <Line type="monotone" dataKey="Active Ops" stroke="#10b981" strokeOpacity={getOpacity(hoveredConn, 'Active Ops')} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Connected Clients" stroke="#3b82f6" strokeOpacity={getOpacity(hoveredConn, 'Connected Clients')} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No connection activity telemetry</span>
            )}
          </div>
        </div>

        {/* Replication Lag */}
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-rose-400" />
              Replication Lag (seconds)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(replLagFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={replLagFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredReplLag} onHoverSeries={setHoveredReplLag} />} />
                  <Line type="monotone" dataKey="Lag (s)" stroke="#ef4444" strokeOpacity={getOpacity(hoveredReplLag, 'Lag (s)')} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No replication lag telemetry</span>
            )}
          </div>
        </div>

        {/* Page Faults Rate */}
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-amber-400" />
              Page Faults Rate (faults/s)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(pageFaultsFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={pageFaultsFormatted}>
                  <defs>
                    <linearGradient id="pageFaultsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredPageFaults} onHoverSeries={setHoveredPageFaults} />} />
                  <Area
                    type="monotone"
                    dataKey="Page Faults/s"
                    stroke="#f59e0b"
                    strokeOpacity={getOpacity(hoveredPageFaults, 'Page Faults/s')}
                    fillOpacity={getOpacity(hoveredPageFaults, 'Page Faults/s')}
                    strokeWidth={2}
                    fill="url(#pageFaultsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No page faults telemetry</span>
            )}
          </div>
        </div>

        {/* Lock Waits */}
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div className="mb-3 border-b border-gray-700/50 pb-2 flex justify-between items-center">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-rose-400" />
              Lock Waits
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(lockWaitsFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lockWaitsFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredLockWaits} onHoverSeries={setHoveredLockWaits} />} />
                  <Line type="monotone" dataKey="Lock Waits" stroke="#ef4444" strokeOpacity={getOpacity(hoveredLockWaits, 'Lock Waits')} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No lock waits telemetry</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MongoCollectionsView;
